import React from "react";
import { useTranslation } from "react-i18next";
import {
    CheckCircle,
    XCircle,
    Mail,
    Phone,
    Shield,
    Edit2,
    Eye,
    EyeOff,
} from "@/components/icons/index";
import { formatDisplayDate } from "@/utils/dateUtils";

const StatusPill = ({ active }) => {
    const { t } = useTranslation();
    return active ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <CheckCircle className="w-3 h-3" /> {t('active')}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
            <XCircle className="w-3 h-3" /> {t('inactive')}
        </span>
    );
};

const Avatar = ({ user }) => {
    const initials =
        [user?.FIRSTNAME?.[0], user?.LASTNAME?.[0]]
            .filter(Boolean)
            .join("")
            .toUpperCase() ||
        user?.USERNAME?.[0]?.toUpperCase() ||
        "U";

    if (
        user?.PROFILEIMAGE &&
        typeof user.PROFILEIMAGE === "string" &&
        user.PROFILEIMAGE.trim() !== ""
    ) {
        return (
            <img
                src={user.PROFILEIMAGE}
                alt={initials}
                className="w-8 h-8 rounded-lg object-cover border border-slate-200 dark:border-slate-600 shrink-0"
                onError={(e) => {
                    e.target.replaceWith(
                        Object.assign(document.createElement("div"), {
                            className:
                                "w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm select-none",
                            textContent: initials,
                        })
                    );
                }}
            />
        );
    }

    return (
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm select-none">
            {initials}
        </div>
    );
};

export const getCustomerColumns = (handlers, helpers = {}) => {
    const { handleEdit } = handlers;
    const {
        visiblePasswords = {},
        togglePasswordVisibility,
        getIndex,
        t,
    } = helpers;

    return [
        {
            key: "_index",
            label: "#",
            width: 56,
            sticky: 'left',
            render: (row, rowIndex) => (
                <span className="text-[11px] font-bold text-slate-400 tabular-nums">
                    {typeof getIndex === "function"
                        ? getIndex(rowIndex)
                        : rowIndex + 1}
                </span>
            ),
        },

        {
            key: "companyLogo",
            label: t("customersPage.columns.logo"),
            sticky: 'left',
            render: (row) => {
                const logo = row?.COMPANYLOGOLIGHT;

                const initials =
                    `${row?.PROFILE_FIRSTNAME?.[0] || ""}${row?.PROFILE_LASTNAME?.[0] || ""
                        }`.toUpperCase();

                return (
                    <div className="w-16 h-12 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden shadow-sm">
                        {logo ? (
                            <img
                                src={logo}
                                alt={`${row?.PROFILE_FIRSTNAME || ""} ${row?.PROFILE_LASTNAME || ""
                                    }`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.parentElement
                                        .querySelector(".logo-fallback")
                                        ?.classList.remove("hidden");
                                }}
                            />
                        ) : null}

                        <span
                            className={`logo-fallback text-[11px] font-bold text-slate-500 dark:text-slate-400 ${logo ? "hidden" : ""
                                }`}
                        >
                            {initials || "?"}
                        </span>
                    </div>
                );
            },
        },

        {
            key: "user",
            label: t("customersPage.columns.user"),
            render: (row) => (
                <div
                    onClick={() => handleEdit?.(row)}
                    className="flex items-center gap-3 cursor-pointer group"
                    title={t("usersPage.editUser")}
                >
                    {/* <Avatar
                        user={{
                            ...row,
                            FIRSTNAME: row?.PROFILE_FIRSTNAME,
                            LASTNAME: row?.PROFILE_LASTNAME,
                            USERNAME: row?.PROFILE_USERNAME,
                        }}
                    /> */}

                    <div className="min-w-0">
                        <p className="font-semibold text-[13px] text-slate-900 dark:text-white truncate group-hover:text-blue-600 transition-colors">
                            {row?.PROFILE_FIRSTNAME}{" "}
                            {row?.PROFILE_LASTNAME}
                        </p>

                        <p className="text-[11px] text-slate-400 font-mono truncate group-hover:text-blue-500 transition-colors">
                            @{row?.PROFILE_USERNAME || "—"}
                        </p>
                    </div>
                </div>
            ),
        },

        {
            key: "contact",
            label: t("customersPage.columns.contact"),
            render: (row) => (
                <div className="space-y-0.5">
                    <p className="text-[12px] text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-blue-400 shrink-0" />

                        <span className="truncate max-w-[160px]">
                            {row?.PROFILE_EMAILID || "—"}
                        </span>
                    </p>
                </div>
            ),
        },

        {
            key: "role",
            label: t("customersPage.columns.role"),
            render: (row) => (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300">
                    <Shield className="w-3 h-3" />
                    {row?.ROLE_NAME || `#${row?.ROLEID || "—"}`}
                </span>
            ),
        },

        {
            key: "USERNAME",
            label: t("customersPage.columns.username"),
            render: (row) => (
                <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400">
                    {row?.PROFILE_USERNAME || "—"}
                </span>
            ),
        },

        {
            key: "password",
            label: t("customersPage.columns.password"),
            render: (row) => {
                const rowKey =
                    row?.CUST_ID ?? row?.PROFILE_USERNAME ?? "";

                const visible = !!visiblePasswords[rowKey];

                return (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            togglePasswordVisibility?.(rowKey);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold"
                        aria-label={
                            visible
                                ? t("customersPage.hidePassword")
                                : t("customersPage.showPassword")
                        }
                    >
                        {visible ? (
                            <EyeOff className="w-3 h-3 text-red-500" />
                        ) : (
                            <Eye className="w-3 h-3 text-green-400" />
                        )}

                        {visible
                            ? row?.PROFILE_PASSWORD || "—"
                            : "*****************"}
                    </button>
                );
            },
        },


        {
            key: "status",
            label: t("customersPage.columns.status"),
            render: (row) => (
                <StatusPill active={row?.ISACTIVE} />
            ),
        },

        {
            key: "CREATED_BY",
            label: t("customersPage.columns.createdBy"),
            render: (row) => (
                <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400">
                    {row?.CREATED_BY || "—"}
                </span>
            ),
        },
        {
            key: "MODIFIED_BY",
            label: t("customersPage.columns.modifiedBy"),
            render: (row) => (
                <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400">
                    {row?.MODIFIED_BY || "—"}
                </span>
            ),
        },
        {
            key: "CREATED_ON",
            label: t("customersPage.columns.createdOn"),
            render: (row) => (
                <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400">
                    {formatDisplayDate(row?.CREATED_ON) || "—"}
                </span>
            ),
        },
        {
            key: "MODIFIED_ON",
            label: t("customersPage.columns.modifiedOn"),
            render: (row) => (
                <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400">
                    {formatDisplayDate(row?.MODIFIED_ON) || "—"}
                </span>
            ),
        },


        {
            key: "actions",
            label: t("customersPage.columns.actions"),
            sticky: "right",
            render: (row) => (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => handleEdit?.(row)}
                        className="w-7 h-7 rounded flex items-center justify-center transition-all bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                        title={t("usersPage.editUser")}
                    >
                        <Edit2 size={14} />
                    </button>
                </div>
            ),
        },
    ];
};

export const getCustomerExportColumns = (t) => [
    {
        key: "PROFILE_FIRSTNAME",
        label: t("customersPage.export.firstName"),
    },
    {
        key: "PROFILE_LASTNAME",
        label: t("customersPage.export.lastName"),
    },
    {
        key: "PROFILE_USERNAME",
        label: t("customersPage.export.username"),
    },
    {
        key: "PROFILE_EMAILID",
        label: t("customersPage.export.email"),
    },
    {
        key: "ROLE_NAME",
        label: t("customersPage.export.role"),
    },
    {
        key: "COMPANYLOGOLIGHT",
        label: t("customersPage.export.companyLogo"),
    },
    {
        key: "ISACTIVE",
        label: t("customersPage.export.status"),
        accessor: (row) =>
            row?.ISACTIVE ? t("active") : t("inactive"),
    },
    {
        key: "CREATED_BY",
        label: t("customersPage.export.createdBy"),
    },
    {
        key: "CREATED_ON",
        label: t("customersPage.export.createdOn"),
    },
    {
        key: "MODIFIED_BY",
        label: t("customersPage.export.modifiedBy"),
    },
    {
        key: "MODIFIED_ON",
        label: t("customersPage.export.modifiedOn"),
    },
];